import { DateTime } from 'luxon';
import React from 'react';
import { TDR } from 'tdr-common';
import { analytics, LINK_CLICKED_EVENT } from '../../helpers/analytics';
import { setLang } from '../../helpers/setLang';
import ArrowOutward from '../../icons/arrow-outward.svg';
import RestaurantEmailLink from '../buttons/RestaurantEmailLink';
import RestaurantPhoneLink from '../buttons/RestaurantPhoneLink';
import { InfoCallout } from './InfoCallout';
import styles from './NoTimeslotsAvailable.module.scss';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '../../helpers/resolveLocalizedText';

export interface NoTimeSlotsAvailableProps {
	restaurant: TDR.Restaurant;
	date: string;
	service: string;
	tableName?: string;
}

export const NoTimeSlotsAvailable = ({ restaurant, service, tableName, date }: NoTimeSlotsAvailableProps) => {
	const { t, i18n } = useTranslation();
	const altPlatform = restaurant.settings?.alternativePlatform?.vendor;
	const altPlatformHref = restaurant.settings?.alternativePlatform?.href;

	let href = altPlatformHref;
	if (i18n.language !== 'en') {
		href = setLang(href, i18n.language);
	}

	const contactByPhone = altPlatform === 'phone';
	const contactByEmail = altPlatform === 'email';

	const canBookWithAltPlatform = !contactByEmail && !contactByPhone && !!altPlatform && !!altPlatformHref;

	const handleThirdPartyRMSClick = () => {
		analytics.track(LINK_CLICKED_EVENT, {
			href: href,
			purpose: 'Redirect to RMS from No Timeslots Available Callout',
			platform: altPlatform
		});
	};

	const getActionText = () => {
		if (contactByPhone && !!restaurant.phoneNumber) {
			return (
				<span className={styles.Block}>
					{t('messages.try_another_time_or_call', { defaultValue: 'Please try another time, or call the restaurant directly at' })} <RestaurantPhoneLink phoneNumber={restaurant.phoneNumber} />.
				</span>
			);
		}

		if (contactByEmail && restaurant.contactEmail) {
			return (
				<span className={styles.Block}>
					{t('messages.try_another_time_or_email', { defaultValue: 'Please try another time, or email the restaurant at' })} <RestaurantEmailLink email={restaurant?.contactEmail} />.
				</span>
			);
		}

		if (canBookWithAltPlatform) {
			return (
				<span className={styles.Block} >
					{t('messages.try_another_time_or_search_on', { defaultValue: 'Please try another time, or search for availability on' })}{' '}
					<a
						href={href}
						target='_blank'
						onClick={handleThirdPartyRMSClick}
						rel='noreferrer'
						className={styles.Link}
					>
						{TDR.RMSDisplayNameMap[altPlatform]} <img src={ArrowOutward} />
					</a>
				</span>
			);
		}
	};

	const formattedDate = DateTime.fromISO(date).setLocale(i18n.language === 'fr' ? 'fr-CA' : 'en-US').toLocaleString({ month: 'long', day: 'numeric' });

	const getTableMessage = () => {
		if (tableName) {
			return t('messages.table_not_available', {
				defaultValue: '{{tableName}} is not available',
				tableName
			});
		}
		return t('messages.no_availability', {
			defaultValue: 'there is no availability'
		});
	};

	const getServiceMessage = () => {
		if (service) {
			return t('messages.service_unavailable', {
				defaultValue: 'for {{serviceName}}',
				serviceName: resolveLocalizedText(service, i18n.language)
			});
		}
		return '';
	};

	return (
		<InfoCallout>
			<span>
				{t('messages.unfortunately_no_availability', {
					defaultValue: 'Unfortunately, {{tableMessage}} {{serviceMessage}} on {{date}} through Tablz.',
					tableMessage: getTableMessage(),
					serviceMessage: getServiceMessage(),
					date: formattedDate
				})}
			</span>

			{getActionText()}
		</InfoCallout>
	);
};
